/**
 * F-505-FE-05: AuthContext
 *
 * JWT lifecycle management:
 * - Access token stored in memory (React state) — NOT localStorage
 * - Refresh via POST /api/auth/refresh (httpOnly cookie carries refresh token)
 * - Role detection: 'doctor' | 'admin' | null
 * - Login/logout actions
 * - isAuthenticated / isLoading state
 * - User profile data
 *
 * Migration note: The legacy apiClient.ts stored tokens in localStorage under
 * 'ci_auth_token'. AuthContext replaces that pattern for new code. Existing note
 * pages that still import apiClient.ts directly will continue to work until
 * migrated, but new code MUST use useAuth().
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type UserRole = 'doctor' | 'admin';

export interface DoctorProfile {
  id: string;
  email: string;
  name: string;
  hospitalId: string;
  hospitalSlug: string;
  primaryLanguageCode: string;
  specializationId: string;
  role: 'doctor';
}

export interface AdminProfile {
  id: string;
  email: string;
  role: 'admin';
}

export type UserProfile = DoctorProfile | AdminProfile;

export interface AuthState {
  accessToken: string | null;
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  role: UserRole | null;
}

// P2.5: MFA required response from login endpoint
export interface MfaRequiredResult {
  mfaRequired: true;
  mfaToken: string;
}

// Result from loginAdmin — indicates the next step the caller must perform.
// Exactly one of these combinations will be true:
//   requirePasswordChange=true   → show change-password form (tempToken provided)
//   mfaRequired=true             → redirect to /admin/mfa/verify (mfaToken provided)
//   mfaSetupRequired=true        → redirect to /admin/mfa/setup (setupToken provided)
//   all false                    → normal login succeeded (accessToken set in context)
export interface AdminLoginResult {
  requirePasswordChange: boolean;
  /** Limited-scope JWT when requirePasswordChange=true. */
  tempToken?: string;
  /** When true: admin has MFA enrolled and must verify TOTP before receiving full token. */
  mfaRequired?: boolean;
  /** Short-lived mfa-only JWT to pass to /admin/auth/mfa/verify-login. */
  mfaToken?: string;
  /** When true: admin has no MFA enrolled and must enroll before receiving full token (post-grace). */
  mfaSetupRequired?: boolean;
  /** Short-lived mfa-setup JWT to pass to /admin/auth/mfa/enroll. */
  setupToken?: string;
  /** When true: enrollment is recommended but not yet enforced. */
  mfaSetupRecommended?: boolean;
  /** Email of the admin (for display in MFA pages). */
  adminEmail?: string;
}

export interface AuthActions {
  loginDoctor: (hospitalSlug: string, email: string, password: string) => Promise<MfaRequiredResult | void>;
  /** Returns AdminLoginResult so callers can branch on requirePasswordChange. */
  loginAdmin: (email: string, password: string) => Promise<AdminLoginResult>;
  logout: () => Promise<void>;
  /** Returns a fresh access token (refreshes if needed). Used by api.ts fetch wrapper. */
  getValidToken: () => Promise<string | null>;
  /** P2.5: Accept a token+user pair from the MFA verify-login endpoint. */
  acceptMfaToken: (accessToken: string, user: UserProfile) => void;
}

export type AuthContextValue = AuthState & AuthActions;

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | null>(null);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const BASE = import.meta.env.VITE_API_URL ?? '';

/** sessionStorage key for admin sessions (admins have no refresh token). */
const ADMIN_SESSION_KEY = 'ci_admin_session';

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: 'include', ...init });
  const ct = res.headers.get('content-type') ?? '';
  const body = ct.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const msg =
      typeof body === 'object' && body !== null && 'message' in body
        ? String((body as { message: unknown }).message)
        : `HTTP ${res.status}`;
    const err = new Error(msg) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  return body as T;
}

interface TokenResponse {
  accessToken: string;
  user: UserProfile;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Keep a stable ref for the token so callbacks don't close over stale state
  const tokenRef = useRef<string | null>(null);
  tokenRef.current = accessToken;

  // Bridge: sync token to localStorage for legacy apiClient.ts consumers
  // (NoteDecisionPage, NoteEditorPage, etc. read from 'ci_auth_token')
  useEffect(() => {
    if (accessToken) {
      localStorage.setItem('ci_auth_token', accessToken);
    } else {
      localStorage.removeItem('ci_auth_token');
    }
  }, [accessToken]);

  // In-flight refresh promise — prevents simultaneous refresh calls
  const refreshPromise = useRef<Promise<string | null> | null>(null);

  // -------------------------------------------------------------------------
  // Refresh access token from httpOnly cookie
  // -------------------------------------------------------------------------
  const refresh = useCallback(async (): Promise<string | null> => {
    if (refreshPromise.current) return refreshPromise.current;

    refreshPromise.current = (async () => {
      try {
        const data = await fetchJson<TokenResponse>(`${BASE}/api/auth/refresh`, {
          method: 'POST',
        });
        setAccessToken(data.accessToken);
        setUser(data.user);
        tokenRef.current = data.accessToken;
        return data.accessToken;
      } catch {
        // Refresh failed — clear auth state
        setAccessToken(null);
        setUser(null);
        tokenRef.current = null;
        return null;
      } finally {
        refreshPromise.current = null;
      }
    })();

    return refreshPromise.current;
  }, []);

  // -------------------------------------------------------------------------
  // On mount: restore admin session from sessionStorage, or attempt doctor refresh
  // -------------------------------------------------------------------------
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(ADMIN_SESSION_KEY);
      if (stored) {
        const { accessToken: token, user: adminUser } = JSON.parse(stored) as { accessToken: string; user: AdminProfile };
        if (token && adminUser?.role === 'admin') {
          setAccessToken(token);
          setUser(adminUser);
          tokenRef.current = token;
          setIsLoading(false);
          return;
        }
      }
    } catch { /* corrupted — fall through to doctor refresh */ }

    refresh().finally(() => setIsLoading(false));
  }, [refresh]);

  // -------------------------------------------------------------------------
  // getValidToken — used by api.ts to get a fresh token before each request
  // -------------------------------------------------------------------------
  const getValidToken = useCallback(async (): Promise<string | null> => {
    if (tokenRef.current) return tokenRef.current;
    return refresh();
  }, [refresh]);

  // -------------------------------------------------------------------------
  // Login actions
  // -------------------------------------------------------------------------
  const loginDoctor = useCallback(
    async (hospitalSlug: string, email: string, password: string): Promise<MfaRequiredResult | void> => {
      const data = await fetchJson<TokenResponse | MfaRequiredResult>(`${BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hospitalSlug, email, password }),
      });
      // P2.5: MFA step — return without setting auth state
      if ('mfaRequired' in data && data.mfaRequired) {
        return data as MfaRequiredResult;
      }
      const tokenData = data as TokenResponse;
      setAccessToken(tokenData.accessToken);
      setUser(tokenData.user);
      tokenRef.current = tokenData.accessToken;
    },
    [],
  );

  // P2.5: Accept a fully-issued token after MFA verify-login
  const acceptMfaToken = useCallback((token: string, userProfile: UserProfile) => {
    setAccessToken(token);
    setUser(userProfile);
    tokenRef.current = token;
  }, []);

  const loginAdmin = useCallback(async (email: string, password: string): Promise<AdminLoginResult> => {
    const data = await fetchJson<{
      accessToken: string;
      requirePasswordChange: boolean;
      tempToken?: string;
      mfaRequired?: boolean;
      mfaToken?: string;
      mfaSetupRequired?: boolean;
      setupToken?: string;
      mfaSetupRecommended?: boolean;
      admin: { id: string; email: string; role: string };
    }>(`${BASE}/admin/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    // Force password change — limited-scope token, no full auth state yet.
    if (data.requirePasswordChange) {
      // Store the limited-scope token via setAccessToken so the api wrapper
      // attaches it to the upcoming /admin/auth/change-password call (the
      // route requires adminAuth Bearer-token middleware). Do NOT set user
      // state — isAuthenticated stays false so the dashboard redirect effect
      // in AdminLoginPage does not fire.
      setAccessToken(data.accessToken);
      return {
        requirePasswordChange: true,
        tempToken: data.tempToken ?? data.accessToken,
        adminEmail: data.admin.email,
      };
    }

    // MFA required — admin has enrolled MFA; must verify TOTP before full token.
    if (data.mfaRequired && data.mfaToken) {
      return {
        requirePasswordChange: false,
        mfaRequired: true,
        mfaToken: data.mfaToken,
        adminEmail: data.admin.email,
      };
    }

    // MFA setup required — post-grace, unenrolled; must enroll before full token.
    if (data.mfaSetupRequired && data.setupToken) {
      return {
        requirePasswordChange: false,
        mfaSetupRequired: true,
        setupToken: data.setupToken,
        adminEmail: data.admin.email,
      };
    }

    // Normal login (or within grace — mfaSetupRecommended may be true).
    const adminUser: AdminProfile = { id: data.admin.id, email: data.admin.email, role: 'admin' };
    setAccessToken(data.accessToken);
    setUser(adminUser);
    tokenRef.current = data.accessToken;

    // Admin auth has no refresh token — persist in sessionStorage so it survives page refreshes
    try {
      sessionStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify({ accessToken: data.accessToken, user: adminUser }));
    } catch { /* quota exceeded — session will be lost on refresh */ }

    return {
      requirePasswordChange: false,
      mfaSetupRecommended: data.mfaSetupRecommended,
      adminEmail: data.admin.email,
    };
  }, []);

  // -------------------------------------------------------------------------
  // Logout
  // -------------------------------------------------------------------------
  const logout = useCallback(async () => {
    try {
      await fetchJson(`${BASE}/api/auth/logout`, { method: 'POST' });
    } catch {
      // Best-effort logout
    } finally {
      setAccessToken(null);
      setUser(null);
      tokenRef.current = null;
      sessionStorage.removeItem(ADMIN_SESSION_KEY);
    }
  }, []);

  // -------------------------------------------------------------------------
  // Derived state
  // -------------------------------------------------------------------------
  const role: UserRole | null = user?.role ?? null;
  const isAuthenticated = accessToken !== null && user !== null;

  const value = useMemo<AuthContextValue>(
    () => ({
      accessToken,
      user,
      isAuthenticated,
      isLoading,
      role,
      loginDoctor,
      loginAdmin,
      logout,
      getValidToken,
      acceptMfaToken,
    }),
    [
      accessToken,
      user,
      isAuthenticated,
      isLoading,
      role,
      loginDoctor,
      loginAdmin,
      logout,
      getValidToken,
      acceptMfaToken,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
